import React, { useMemo, useReducer, useRef, forwardRef } from 'react';
import { useIntl } from 'umi';
import { Select, Text, Table, TableColumn, List, Checkbox, Button, Bubble } from 'tea-component';
import UploadFile from '../UploadFile';
import { IUploadFileRef } from '../UploadFile/types';
import { getPreviewData } from "@/pages/datalab/pages/Number/PersonalData/service";
import Abnormal from "@home/components/Abnormal";
import { 
  TDelimiterKey, 
  TColumn, 
  ISheetPreview, 
  TMode, 
  TState, 
  IFileState, 
  TAction, 
  IUploadTableFileProps 
} from './types';
import './index.scss';

const initialState: TState = {
  delimiterKey: 'comma',
  sheets: [],
  activeSheet: '',
  checkedSheets: [],
  loading: false,
  error: null,
};

function reducer(state: TState, action: TAction): TState {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'PREVIEW_SUCCESS': {
      const { sheets, mode } = action.payload;
      const first = sheets?.[0]?.sheet_name || '';
      return {
        ...state,
        sheets,
        activeSheet: first,
        checkedSheets: mode === 'excel' ? [] : [first],
      };
    }
    case 'SET_DELIMITER':
      return { ...state, delimiterKey: action.payload };
    case 'SET_ACTIVE_SHEET':
      return { ...state, activeSheet: action.payload };
    case 'TOGGLE_SHEET': {
      const { name, checked } = action.payload;
      const exists = state.checkedSheets.includes(name);
      let next = state.checkedSheets.slice();
      if (checked && !exists) next.push(name);
      if (!checked && exists) next = next.filter((n) => n !== name);
      return { ...state, checkedSheets: next };
    }
    case 'CHECK_ALL_SHEETS': {
      if (!state.sheets.length) return state;
      const next: string[] = [];
      if (action.payload) {
        // 说明是全选
        state.sheets.forEach(sheet => {
          if (sheet.columns) { // 有值代表sheet页面有数据, 否则是一个空sheet, 不能被选择
            next.push(sheet.sheet_name);
          }
        });
      }
      return { ...state, checkedSheets: next };
    }
    default:
      return state;
  }
}

const { scrollable, autotip } = Table.addons;

const UploadTableFile = forwardRef<IUploadFileRef, IUploadTableFileProps>(({
  defaultState,
  gameCode,
  onNextStep,
  onDeleteFile,
}, ref) => {
  const { formatMessage } = useIntl();
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...defaultState
  });
  
  // 新增：使用ref存储文件相关状态
  const fileStateRef = useRef<IFileState | null>({
    file: defaultState?.file,
    fileUrlInfo: defaultState?.fileUrlInfo,
    mode: defaultState?.mode || 'idle',
    uploadDate: defaultState?.uploadDate || ''
  });
  
  const isCsv = fileStateRef.current?.mode === 'csv';
  const isExcel = fileStateRef.current?.mode === 'excel';

  const requestPreview = async (params): Promise<void> => {
    if (!fileStateRef.current?.file || fileStateRef.current?.mode === 'idle') return;
    dispatch({ type: 'SET_LOADING', payload: true });
    const allParams = {
      file_path: fileStateRef.current?.fileUrlInfo?.url || fileStateRef.current?.fileUrlInfo?.key || '',
      file_type: fileStateRef.current?.mode === 'excel' ? 'excel' : 'csv',
      split: state.delimiterKey,
      game_code: gameCode,
      file_size: fileStateRef.current?.file?.size,
      ...params,
    };

    try {
      const res = await getPreviewData(allParams);
      const arr: ISheetPreview[] = (res as any)?.datas || [];
      if (fileStateRef.current?.mode === 'csv' && arr?.[0]) {
        // 使用正则表达式去除文件名中的最后一个点及其后缀（如 .csv/.xlsx）
        // \.[^/.]+$ 匹配最后一个点开始到字符串结尾的内容
        arr[0].sheet_name = fileStateRef.current?.file?.name?.replace(/\.[^/.]+$/, '') || 'csv文件';
      }
      dispatch({ type: 'PREVIEW_SUCCESS', payload: { sheets: arr, mode: fileStateRef.current?.mode } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const tableColumns: TableColumn[] = useMemo(() => {
    const cols: TColumn[] = state.sheets.find((sheet) => sheet.sheet_name === state.activeSheet)?.columns || [];
    return cols.map((column) => ({ 
      key: column.column_name, 
      width: 200, 
      header: column.column_name, 
      render: (record: any) => record?.[column.column_name] || '-' 
    }));
  }, [state.activeSheet, state.sheets]);

  const tableRecords = useMemo(() => state.sheets.find((s) => s.sheet_name === state.activeSheet)?.records || [], [state.activeSheet, state.sheets]);

  // 计算sheet页面 全选或者半选状态
  const { allChecked, indeterminate } = useMemo(() => {
    if (!state.sheets.length) return { allChecked: false, indeterminate: false };
    const checkedCount = state.checkedSheets.length;
    const total = state.sheets.reduce((acc, sheet) => {
      // sheet页面不是空白的时候 加1
      if (sheet.columns) acc += 1;
      return acc;
    }, 0);
    const indeterminate = checkedCount > 0 && checkedCount < total;
    const allChecked = checkedCount === total;
    return { allChecked, indeterminate };
  }, [state.checkedSheets, state.sheets]);

  const handleUploadSuccess = (fileUrlInfo: any, file: File, uploadDate: string): void => {
    const ext = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    const mode: TMode = ext === 'xlsx' || ext === 'xls' ? 'excel' : 'csv';
    // 存储到ref对象中
    fileStateRef.current = {
      file,
      fileUrlInfo,
      mode,
      uploadDate
    };
    // 发起预览请求
    requestPreview();
  };

  const handleNextStep = (): void => {
    // 合并state和fileStateRef.current传递给onNextStep
    onNextStep?.({
      ...state,
      ...fileStateRef.current
    });
  };
  return (
    <div className="dbl-upload-table-file__first">
      <UploadFile
        ref={ref}
        tips={formatMessage({ id: 'dbl_personal_data_support_format' })}
        failText={formatMessage({ id: 'dbl_personal_data_support_format' })}
        onSuccess={handleUploadSuccess}
        onError={() => {
          fileStateRef.current = {mode: 'idle'};
          dispatch({ type: 'RESET' });
        }}
        onDelete={() => {
          onDeleteFile?.();
          fileStateRef.current = {mode: 'idle'};
          dispatch({ type: 'RESET' });
        }}
        // 修改：使用对象参数传递默认状态
        defaultState={{
          file: fileStateRef.current?.file,
          fileUrlInfo: fileStateRef.current?.fileUrlInfo,
          mode: fileStateRef.current?.mode,
          uploadDate: fileStateRef.current?.uploadDate
        }}
      />

      {/* csv/txt 分隔符（仅 csv/txt 且已选择文件时显示） */}
      {fileStateRef.current?.file && isCsv ? (
        <div className="dbl-upload-table-file__selector">
          <Select
            value={state.delimiterKey}
            onChange={(delimiterKey: TDelimiterKey) => {
              dispatch({ type: 'SET_DELIMITER', payload: delimiterKey });
              requestPreview({split: delimiterKey});
            }}
            appearance="button"
            options={[
              { value: 'comma', text: formatMessage({ id: 'dbl_personal_data_comma' }) },
              { value: 'semicolon', text: formatMessage({ id: 'dbl_personal_data_semicolon' }) },
              { value: 'equal', text: formatMessage({ id: 'dbl_personal_data_equal' }) },
              { value: 'colon', text: formatMessage({ id: 'dbl_personal_data_colon' }) },
              { value: 'space', text: formatMessage({ id: 'dbl_personal_data_space' }) },
            ]}
            matchButtonWidth
            style={{ minWidth: 260 }}
            button={(selectedOption) => <><Text theme="weak" style={{ marginRight: 8 }}>{formatMessage({ id: 'dbl_personal_data_choose_delimiter' })}</Text> {selectedOption.text}</>}
          />
        </div>
      ) : null}

      {/* 预览容器：占满宽度，高度与边框由样式控制 */}
      {
        fileStateRef.current?.mode === 'idle' ? '' : (
          <Abnormal
            style={{
              height: '100%',
              border: '1px solid #EBECF1',
              marginTop: '20px',
              marginBottom: '20px',
            }}
            isLoading={state.loading}
            isError={!!state.error}
            isNoData={state?.sheets?.length === 0}
            component={
              <div className="dbl-upload-table-file__preview-content">
                {isExcel && (
                  <div className="dbl-upload-table-file__excel-left">
                    <div className="dbl-upload-table-file__excel-header">{formatMessage({id: 'dbl_personal_data_sheets_selection'})}</div>
                    <List className="dbl-upload-table-file__excel-list" type="option-group">
                      <List.GroupLabel>
                        <Checkbox
                          value={allChecked}
                          indeterminate={indeterminate as any}
                          onChange={(v) => dispatch({ type: 'CHECK_ALL_SHEETS', payload: v as boolean })}
                        />
                        <span className="dbl-upload-table-file__excel-filename"
                          title={fileStateRef.current?.file?.name}>{fileStateRef.current?.file?.name || ''}</span>
                      </List.GroupLabel>
                      {(state.sheets || []).map((s) => (
                        <List.Item
                          key={s.sheet_name}
                          className="dbl-upload-table-file__excel-item"
                          selected={s.sheet_name === state.activeSheet}
                          onClick={() => dispatch({ type: 'SET_ACTIVE_SHEET', payload: s.sheet_name })}
                          title={s.sheet_name}
                        >
                          <Checkbox
                            disabled={!s.columns}
                            value={state.checkedSheets.includes(s.sheet_name)}
                            onChange={(v) => dispatch({
                              type: 'TOGGLE_SHEET',
                              payload: { name: s.sheet_name, checked: v as boolean }
                            })}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          />
                          {s.sheet_name}
                        </List.Item>
                      ))}
                    </List>
                  </div>
                )}
                {
                  // (<Status
                  //   icon={null}
                  //   title={"暂无数据"}
                  //   description="文件没有内容"
                  // />)
                }
                <div className="dbl-upload-table-file__excel-right">
                  <Table
                    recordKey={(r: any, i: number) => String(i)}
                    columns={tableColumns}
                    records={tableRecords}
                    addons={[
                      scrollable({
                        maxHeight: 'calc(100% - 50px)',
                        minWidth: 600,
                      }),
                      autotip({
                        errorText: formatMessage({id: 'dbl_personal_data_sheet_empty'}),
                        isError: tableColumns?.length < 1,
                        style: { lineHeight: '320px' },
                        emptyText: formatMessage({ id: 'dbl_common_no_data' }),
                      })
                    ]}
                    className="dbl-upload-table-file__table"
                  />
                </div>
              </div>
            }
          />
        )
      }

      {/* Next Step 按钮 */}
      <div className="dbl-upload-table-file__first__actions">
        <Bubble
          content={(isExcel && state.checkedSheets?.length === 0) ? formatMessage({id: 'dbl_personal_data_select_sheets'}) : undefined}
        >
          <Button
            type="primary"
            disabled={state.checkedSheets?.length === 0}
            onClick={handleNextStep}
          >
            {formatMessage({ id: 'dbl_personal_data_next_step' })}
          </Button>
        </Bubble>
      </div>
    </div>
  );
});

export default UploadTableFile;
